import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  CheckIcon,
  EllipsisVerticalIcon,
  StarIcon as StarOutlineIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { Copy } from "lucide-react";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import { DisabledActionTooltip } from "components/Environment/shared/DisabledActionTooltip";
import { getComputeCardActionButtonClassName } from "./computeCardActionStyles";
import {
  computeRackHoneycombPatternStyle,
  rackSurfaceStyle,
} from "components/Environment/shared/ComputeDetail/rackSurface";
import { classNames } from "components/utils";
import {
  isStarredComputeKey,
  toggleStarredCompute,
  useStarredCompute,
} from "../../hooks/useStarredCompute";

export interface ComputeCardMetadata {
  label?: string;
  standalonePill?: boolean;
  unstyled?: boolean;
  value: React.ReactNode;
}

export interface ComputeCardTemplateProps {
  /** A denser layout for narrow panes and small-screen card lists. */
  compact?: boolean;
  /** Switch to the compact layout at `lg` and in narrow application-shell panes. */
  compactAtMedium?: boolean;
  borderClassName?: string;
  borderWidthClassName?: string;
  borderRadiusClassName?: string;
  compactSurfaceStyle?: React.CSSProperties;
  stackedSurfaceStyle?: React.CSSProperties;
  surfaceStyle?: React.CSSProperties;
  surfaceBackgroundFilter?: React.CSSProperties["filter"];
  statusActionClusterStyle?: React.CSSProperties;
  statusActionClusterBackgroundFilter?: React.CSSProperties["filter"];
  metadataSurfaceBackgroundClassName?: string;
  dividerClassName?: string;
  onOpen?: () => void;
  title: React.ReactNode;
  starredComputeKey?: string;
  secondaryTitle?: React.ReactNode;
  secondaryTitleClassName?: string;
  metadata: ComputeCardMetadata[];
  hardware: React.ReactNode;
  responsiveHardware?: React.ReactNode;
  status?: React.ReactNode;
  primaryAction?: React.ReactNode;
  onCopySsh: () => void;
  sshControlsDisabledReason?: string;
  copiedControl: "ssh" | null;
  copySuccessClassName: string;
  menu: React.ReactNode;
}

interface ComputeCardMenuProps {
  label: string;
  triggerClassName?: string;
  triggerContent?: React.ReactNode;
  children: React.ReactNode;
}

export const SSH_NOT_ENABLED_REASON = "SSH is not enabled on this node.";
const STACK_COMPUTE_CARD_ACTIONS_WIDTH = 640;
const COMPUTE_CARD_ACTIONS_SECTION_MIN_WIDTH_CLASS_NAME = "min-w-[320px]";

const splitBackgroundLayerStyle = ({
  background,
  backgroundColor,
  backgroundImage,
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
  ...foregroundStyle
}: React.CSSProperties) => ({
  backgroundLayerStyle: {
    background,
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize,
  },
  foregroundStyle,
});

const splitStatusActionBackgroundLayerStyle = (style: React.CSSProperties) => {
  const { backgroundLayerStyle, foregroundStyle } =
    splitBackgroundLayerStyle(style);
  const { boxShadow, ...statusActionForegroundStyle } = foregroundStyle;

  return {
    backgroundLayerStyle: {
      ...backgroundLayerStyle,
      boxShadow,
    },
    foregroundStyle: statusActionForegroundStyle,
  };
};

export const ComputeCardMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & {
    label: string;
  }
>(({ children, label, className, disabled, ...buttonProps }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    disabled={disabled}
    className={getComputeCardActionButtonClassName({
      className: `w-7 !px-0 ${className || ""}`,
      disabled,
    })}
    {...buttonProps}
  >
    {children ?? <EllipsisVerticalIcon className="h-4 w-4" />}
  </button>
));

ComputeCardMenuTrigger.displayName = "ComputeCardMenuTrigger";

export const ComputeCardMenu: React.FC<ComputeCardMenuProps> = ({
  label,
  triggerClassName,
  triggerContent,
  children,
}) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <ComputeCardMenuTrigger label={label} className={triggerClassName}>
        {triggerContent}
      </ComputeCardMenuTrigger>
    </DropdownMenuTrigger>
    <DropdownMenuContent side="bottom" align="end">
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const ComputeCardTemplate: React.FC<ComputeCardTemplateProps> = ({
  compact = false,
  compactAtMedium = false,
  borderClassName,
  borderWidthClassName = "border-2",
  borderRadiusClassName = "rounded-[2px]",
  compactSurfaceStyle,
  stackedSurfaceStyle,
  surfaceStyle,
  surfaceBackgroundFilter,
  statusActionClusterStyle,
  statusActionClusterBackgroundFilter,
  metadataSurfaceBackgroundClassName = "bg-[#242424]",
  dividerClassName,
  onOpen,
  title,
  starredComputeKey,
  secondaryTitle,
  secondaryTitleClassName,
  metadata,
  hardware,
  responsiveHardware,
  status,
  primaryAction,
  onCopySsh,
  sshControlsDisabledReason,
  copiedControl,
  copySuccessClassName,
  menu,
}) => {
  const resolvedBorderClassName = borderClassName ?? "border-[#1e1e1e]";
  const headerRef = useRef<HTMLDivElement>(null);
  const [isMediumOrSmaller, setIsMediumOrSmaller] = useState(false);
  const [isNarrowContainer, setIsNarrowContainer] = useState(false);
  const [shouldStackActions, setShouldStackActions] = useState(false);

  useEffect(() => {
    if (!compactAtMedium || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    const updateMatch = () => setIsMediumOrSmaller(mediaQuery.matches);
    updateMatch();
    mediaQuery.addEventListener("change", updateMatch);
    return () => mediaQuery.removeEventListener("change", updateMatch);
  }, [compactAtMedium]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const updateDimensions = () => {
      setIsNarrowContainer(header.clientWidth <= 960);
      setShouldStackActions(
        header.clientWidth <= STACK_COMPUTE_CARD_ACTIONS_WIDTH,
      );
    };
    updateDimensions();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(updateDimensions);
    observer?.observe(header);
    return () => observer?.disconnect();
  }, []);

  const stopNavigation = (event: React.MouseEvent<HTMLButtonElement>) =>
    event.stopPropagation();
  const sshControlsDisabled = Boolean(sshControlsDisabledReason);
  const starredComputeEnabled =
    useIdentityFeatureFlagEnabled(
      POSTHOG_IDENTITY_FLAGS.STARRED_COMPUTE,
      false,
    ) === true;
  const starredCompute = useStarredCompute(starredComputeKey);
  const showStarredComputeControl =
    starredComputeEnabled && isStarredComputeKey(starredComputeKey);
  const compactLayout =
    compact || (compactAtMedium && (isMediumOrSmaller || isNarrowContainer));
  const stackActionsBelowDetails = compactLayout && shouldStackActions;
  const resolvedSurfaceStyle =
    stackActionsBelowDetails && stackedSurfaceStyle
      ? stackedSurfaceStyle
      : compactLayout && compactSurfaceStyle
        ? compactSurfaceStyle
        : (surfaceStyle ?? rackSurfaceStyle);
  const filteredSurfaceStyles = surfaceBackgroundFilter
    ? splitBackgroundLayerStyle(resolvedSurfaceStyle)
    : undefined;
  const resolvedStatusActionClusterStyle =
    statusActionClusterStyle ?? computeRackHoneycombPatternStyle;
  const showStatusActionClusterSurface = !stackActionsBelowDetails;
  const filteredStatusActionClusterStyles =
    showStatusActionClusterSurface && statusActionClusterBackgroundFilter
      ? splitStatusActionBackgroundLayerStyle(resolvedStatusActionClusterStyle)
      : undefined;
  const stackedActionButtonClassName = "!h-9 !w-full !max-w-none flex-1 !px-3";
  const sshActionButtonClassName = getComputeCardActionButtonClassName({
    className: `${sshControlsDisabled ? "" : "group/copy"} ${
      stackActionsBelowDetails ? stackedActionButtonClassName : "w-12"
    }`,
    disabled: sshControlsDisabled,
  });
  const contentColumnsClassName = compactLayout
    ? `grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 ${
        stackActionsBelowDetails ? "gap-y-0 p-2" : "gap-y-2 pl-2"
      }`
    : "flex flex-wrap items-stretch gap-x-4 gap-y-3 lg:flex-nowrap";
  const identityClassName = compactLayout
    ? `${stackActionsBelowDetails ? "col-span-2" : ""} col-start-1 row-start-1 min-w-0 self-start leading-[1.3]`
    : "min-w-0 flex-1 self-stretch leading-[1.3]";
  const identityInnerClassName = compactLayout
    ? stackActionsBelowDetails
      ? "flex h-full min-w-0 flex-col justify-center gap-3 px-3 pb-0 pt-2.5"
      : "flex h-full min-w-0 flex-col justify-center gap-3 px-3 py-4"
    : "flex h-full min-w-0 flex-col justify-center gap-3 px-4 py-4";
  const titleRowClassName = `flex min-w-0 items-center gap-2 ${stackActionsBelowDetails && showStarredComputeControl ? "pr-8" : ""}`;
  const metadataRowClassName =
    "flex min-w-0 max-w-full flex-wrap items-center gap-1.5";
  const metadataSurfaceClassName = `${metadataSurfaceBackgroundClassName} shadow-[inset_0_2px_5px_rgba(0,0,0,0.2),inset_0_-1px_0_rgba(255,255,255,0.055)]`;
  const metadataClassName = `flex min-h-5 w-fit max-w-full flex-wrap items-center rounded-md px-1 py-0.5 leading-[20px] ${metadataSurfaceClassName}`;
  const metadataPillClassName = `flex min-h-5 shrink-0 items-center rounded-md px-2 py-1 leading-[20px] ${metadataSurfaceClassName}`;
  const hardwareColumnClassName = compactLayout
    ? "hidden"
    : "relative -mr-4 flex shrink-0 self-stretch items-stretch leading-[1.3]";
  const identityHardwareDividerClassName = compactLayout
    ? "hidden"
    : `my-4 -mr-4 hidden w-px shrink-0 self-stretch md:block ${dividerClassName || "bg-zinc-800"}`;
  const metadataDividerClassName = dividerClassName || "bg-[#2e2e2e]";
  const statusActionClusterClassName = compactLayout
    ? stackActionsBelowDetails
      ? "col-span-2 col-start-1 row-start-2 flex w-full min-w-0 flex-col items-stretch justify-start gap-2 leading-[1.3]"
      : `relative col-start-2 row-span-2 row-start-1 ml-auto flex h-full max-w-full shrink-0 self-stretch justify-self-end flex-col items-end justify-start gap-3 overflow-hidden rounded-[2px] border border-zinc-700/20 py-4 pl-3 pr-3 leading-[1.3] ${COMPUTE_CARD_ACTIONS_SECTION_MIN_WIDTH_CLASS_NAME}`
    : `relative flex shrink-0 self-stretch flex-col items-end justify-around gap-2 overflow-hidden rounded-[2px] border border-zinc-700/20 py-4 pl-4 pr-4 xl:w-[320px] ${COMPUTE_CARD_ACTIONS_SECTION_MIN_WIDTH_CLASS_NAME}`;
  const getMetadataItemClassName = (
    index: number,
    removeHorizontalPadding: boolean,
  ) => {
    const horizontalPadding = removeHorizontalPadding
      ? ""
      : index === 0
        ? compactLayout
          ? "pl-0 pr-2"
          : "pl-0 pr-2.5"
        : compactLayout
          ? "px-2"
          : "px-2.5";

    return `flex h-5 min-w-0 shrink-0 items-center gap-1 text-sm leading-none text-secondary ${horizontalPadding}`;
  };
  const unstyledMetadata = metadata.filter(({ unstyled }) => Boolean(unstyled));
  const standaloneMetadata = metadata.filter(
    ({ standalonePill, unstyled }) => standalonePill && !unstyled,
  );
  const groupedMetadata = metadata.filter(
    ({ standalonePill, unstyled }) => !standalonePill && !unstyled,
  );
  const renderMetadataItem = (
    { label, value }: ComputeCardMetadata,
    index: number,
    removeHorizontalPadding = false,
  ) => {
    return (
      <div key={label || index} className="flex shrink-0 items-center">
        {index > 0 ? (
          <span
            aria-hidden="true"
            className={`flex h-5 shrink-0 items-center ${removeHorizontalPadding ? "mr-1.5" : ""}`}
          >
            <span className={`h-4 w-px shrink-0 ${metadataDividerClassName}`} />
          </span>
        ) : null}
        <div
          className={getMetadataItemClassName(index, removeHorizontalPadding)}
        >
          {label ? (
            <span
              className={`inline-flex h-5 shrink-0 items-center leading-none tracking-normal text-secondary ${label === "Created" ? "" : "mr-0.5 text-xs font-bold uppercase"}`}
            >
              {label}
            </span>
          ) : null}
          <span className="inline-flex h-5 min-w-0 items-center leading-none">
            {value}
          </span>
        </div>
      </div>
    );
  };
  const actionRowClassName = stackActionsBelowDetails
    ? "relative z-10 flex h-9 w-full min-w-0 shrink-0 items-stretch justify-stretch gap-2 leading-none"
    : "relative z-10 flex h-7 w-full shrink-0 items-center justify-end gap-1.5 leading-none";
  const primaryActionContainerClassName = stackActionsBelowDetails
    ? "min-w-0 flex-[2_1_0%] overflow-hidden [&_a]:!h-9 [&_a]:!w-full [&_a]:!max-w-none [&_button]:!h-9 [&_button]:!w-full [&_button]:!max-w-none"
    : "min-w-0 max-w-[200px] shrink-0 overflow-hidden";
  const menuContainerClassName = stackActionsBelowDetails
    ? "min-w-0 flex-1 [&_button]:!h-9 [&_button]:!w-full [&_button]:!max-w-none [&_button]:!px-3"
    : "";
  const statusRowClassName = stackActionsBelowDetails
    ? "relative z-10 flex w-full min-w-0 shrink-0 items-center justify-start px-3 py-2 text-left leading-none"
    : "relative z-10 flex w-full min-w-0 shrink-0 items-center justify-end text-right leading-none";
  const statusRowInnerClassName = stackActionsBelowDetails
    ? "flex w-full min-w-0 items-center justify-start"
    : "flex min-w-0 items-center justify-end";
  const stackedMenu =
    stackActionsBelowDetails &&
    React.isValidElement<ComputeCardMenuProps>(menu) &&
    menu.type === ComputeCardMenu
      ? React.cloneElement(menu, {
          triggerClassName: [
            menu.props.triggerClassName,
            stackedActionButtonClassName,
          ]
            .filter(Boolean)
            .join(" "),
          triggerContent: "Actions",
        })
      : menu;

  const actionRow = (
    <div data-testid="compute-card-action-row" className={actionRowClassName}>
      {primaryAction ? (
        <div className={primaryActionContainerClassName}>{primaryAction}</div>
      ) : null}
      <DisabledActionTooltip
        message={sshControlsDisabledReason}
        className={
          stackActionsBelowDetails
            ? "flex min-w-0 flex-1 cursor-not-allowed"
            : undefined
        }
        stopPropagation
      >
        <button
          type="button"
          aria-label="Copy SSH CLI command"
          disabled={sshControlsDisabled}
          onClick={(event) => {
            stopNavigation(event);
            if (!sshControlsDisabled) onCopySsh();
          }}
          className={sshActionButtonClassName}
        >
          {copiedControl === "ssh" ? (
            <CheckIcon className={`h-3.5 w-3.5 ${copySuccessClassName}`} />
          ) : stackActionsBelowDetails ? (
            <span className="inline-flex min-w-0 items-center justify-center truncate">
              Copy SSH
            </span>
          ) : sshControlsDisabled ? (
            <span className="inline-flex w-6 items-center justify-center">
              SSH
            </span>
          ) : (
            <span className="relative inline-flex h-4 w-6 items-center justify-center">
              <span className="absolute inset-0 inline-flex items-center justify-center group-hover/copy:hidden">
                SSH
              </span>
              <Copy className="absolute hidden h-3.5 w-3.5 group-hover/copy:block" />
            </span>
          )}
        </button>
      </DisabledActionTooltip>
      <div
        className={menuContainerClassName}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {stackedMenu}
      </div>
    </div>
  );
  const statusRow = (
    <div data-testid="compute-card-status-row" className={statusRowClassName}>
      <div className={statusRowInnerClassName}>{status}</div>
    </div>
  );
  const starredComputeControl = showStarredComputeControl ? (
    <button
      type="button"
      aria-label={starredCompute ? "Unstar compute" : "Star compute"}
      aria-pressed={starredCompute}
      className={
        stackActionsBelowDetails
          ? "absolute right-3 top-3 z-30 inline-flex h-7 w-7 items-center justify-center rounded-sm text-[rgba(255,255,255,.5)] transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0b0b]"
          : `mt-[-1px] inline-flex h-5 shrink-0 items-center justify-center overflow-hidden rounded-sm transition-[color,opacity] duration-150 hover:text-white focus-visible:ml-2 focus-visible:w-5 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0b0b] ${
              starredCompute
                ? "ml-2 w-5 text-white opacity-100"
                : "ml-0 w-0 text-[rgba(255,255,255,.5)] opacity-0 group-hover:ml-2 group-hover:w-5 group-hover:opacity-100"
            }`
      }
      onClick={(event) => {
        event.stopPropagation();
        toggleStarredCompute(starredComputeKey);
      }}
    >
      {starredCompute ? (
        <StarSolidIcon
          className="h-[18px] w-[18px] shrink-0 fill-current"
          strokeWidth={0}
          aria-hidden="true"
        />
      ) : (
        <StarOutlineIcon
          className="h-[18px] w-[18px] shrink-0"
          strokeWidth={2}
          aria-hidden="true"
        />
      )}
    </button>
  ) : null;

  return (
    <article
      className={`group relative isolate flex w-full max-w-[1400px] overflow-hidden ${borderRadiusClassName} ${borderWidthClassName} font-sans transition duration-200 ease-out [&_.font-mono]:!font-sans ${
        onOpen
          ? "cursor-pointer hover:scale-[1.006] hover:brightness-110 hover:shadow-[0_0_24px_rgba(255,255,255,0.2)] hover:outline hover:outline-1 hover:outline-zinc-600"
          : "cursor-default"
      } ${resolvedBorderClassName}`}
      style={filteredSurfaceStyles?.foregroundStyle ?? resolvedSurfaceStyle}
    >
      {filteredSurfaceStyles ? (
        <div
          aria-hidden="true"
          data-testid="compute-card-surface-background"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            ...filteredSurfaceStyles.backgroundLayerStyle,
            ...filteredSurfaceStyles.foregroundStyle,
            filter: surfaceBackgroundFilter,
          }}
        />
      ) : null}
      {stackActionsBelowDetails ? starredComputeControl : null}
      <div className="relative z-10 min-w-0 flex-1">
        <div ref={headerRef} className="w-full text-left" onClick={onOpen}>
          <div className={contentColumnsClassName}>
            <div className={identityClassName}>
              <div
                data-testid="compute-card-identity-stack"
                className={identityInnerClassName}
              >
                <div
                  data-testid="compute-card-title-row"
                  className={titleRowClassName}
                >
                  <div className="flex min-w-0 flex-1 flex-wrap items-center mt-1">
                    <div className="flex min-w-0 max-w-full shrink-0 items-center truncate text-md font-bold text-white">
                      <span
                        className={classNames(
                          "inline-flex min-w-0 max-w-full items-center truncate",
                          secondaryTitle ? "mr-1.5" : "",
                        )}
                      >
                        {title}
                      </span>
                    </div>
                    {secondaryTitle ? (
                      <span
                        className={`mt-0.5 inline-flex shrink-0 items-center text-xs leading-none text-secondary ${secondaryTitleClassName || ""}`}
                      >
                        {secondaryTitle}
                      </span>
                    ) : null}
                    {!stackActionsBelowDetails ? starredComputeControl : null}
                  </div>
                </div>
                <div
                  data-testid="compute-card-metadata-row"
                  className={metadataRowClassName}
                >
                  {unstyledMetadata.map((item, index) => (
                    <React.Fragment key={item.label || `unstyled-${index}`}>
                      {renderMetadataItem(item, index, true)}
                    </React.Fragment>
                  ))}
                  {standaloneMetadata.length > 0 ? (
                    <div className="flex w-fit max-w-full shrink-0 flex-wrap items-center gap-1.5">
                      {standaloneMetadata.map((item, index) => (
                        <div
                          key={item.label || `standalone-${index}`}
                          className={metadataPillClassName}
                        >
                          {renderMetadataItem(item, 0, true)}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {groupedMetadata.length > 0 ? (
                    <div className={metadataClassName}>
                      {groupedMetadata.map((item, index) =>
                        renderMetadataItem(item, index),
                      )}
                    </div>
                  ) : null}
                </div>
                {compactLayout && responsiveHardware ? (
                  <div
                    data-testid="compute-card-responsive-hardware"
                    className="min-w-0 truncate text-sm font-semibold leading-5 text-white"
                  >
                    {responsiveHardware}
                  </div>
                ) : null}
              </div>
            </div>
            {hardware ? (
              <>
                <div className={identityHardwareDividerClassName} />
                <div
                  data-testid="compute-card-hardware"
                  className={hardwareColumnClassName}
                >
                  <div className="h-full w-full">{hardware}</div>
                </div>
              </>
            ) : null}
            <div
              data-testid="compute-card-status-actions"
              className={statusActionClusterClassName}
              style={
                showStatusActionClusterSurface
                  ? (filteredStatusActionClusterStyles?.foregroundStyle ??
                    resolvedStatusActionClusterStyle)
                  : undefined
              }
            >
              {filteredStatusActionClusterStyles ? (
                <div
                  aria-hidden="true"
                  data-testid="compute-card-status-actions-background"
                  className="pointer-events-none absolute inset-0 z-0"
                  style={{
                    ...filteredStatusActionClusterStyles.backgroundLayerStyle,
                    filter: statusActionClusterBackgroundFilter,
                  }}
                />
              ) : null}
              {stackActionsBelowDetails ? (
                <>
                  {statusRow}
                  {actionRow}
                </>
              ) : (
                <>
                  {actionRow}
                  {statusRow}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export const CompactComputeCardTemplate: React.FC<
  Omit<ComputeCardTemplateProps, "compact">
> = (props) => <ComputeCardTemplate {...props} compact />;
